import { World } from './world';
import { GameMap, type MapData } from './map';
import type { MobileState } from './mobile';
import type { Command } from './commands';
import { COMPONENT_DESCRIPTORS, COMPONENT_KEYS } from './componentDescriptors';
import type {
  BanishComponent,
  ChestComponent,
  DeathComponent,
  DiceComponent,
  DoorComponent,
  EscortComponent,
  Entity,
  EntityId,
  HealthComponent,
  InteractionComponent,
  DungeonGlobeComponent,
  DungeonWaypointComponent,
  DungeonLootComponent,
  TrainingLoopComponent,
  MoongateComponent,
  PickupComponent,
  PositionComponent,
  ProjectileComponent,
  XpGemComponent,
  RenderableComponent,
  TeleporterComponent,
} from './entity';

/**
 * Lossless serialization of a {@link World} to and from a plain, JSON-able
 * snapshot, and back into an equivalent live world.
 *
 * This is the format the authoritative server broadcasts (the wire snapshot is
 * built from it) and would persist. It lives in `sim/` (it imports no rendering
 * and reads no wall clock), so it stays on the authoritative side of the hard
 * architectural split.
 *
 * A snapshot captures *everything* needed to reproduce the world exactly: the
 * map, every entity with all its components, the seeded RNG's stream position,
 * the entity-id allocator, and any pending (undrained) commands. The one
 * non-plain component, the {@link Mobile} movement state machine, is captured
 * via its {@link Mobile.toState} (config plus full mid-step interpolation), so a
 * world serialized mid-walk deserializes mid-walk and continues identically.
 */

/**
 * An entity captured as plain data: the same component fields as {@link Entity},
 * except the live {@link Mobile} class instance is replaced by its plain
 * {@link MobileState} snapshot.
 */
export interface EntitySnapshot {
  id: EntityId;
  position?: PositionComponent;
  renderable?: RenderableComponent;
  mobile?: MobileState;
  /** Door state for door entities; plain data, captured verbatim. */
  door?: DoorComponent;
  /** Dice-prop state for the dice-and-cup (ADR-0083); plain data, captured verbatim. */
  dice?: DiceComponent;
  /** Teleporter destination for teleporter entities; plain data, captured verbatim. */
  teleporter?: TeleporterComponent;
  /**
   * Moongate state for moongate entities (PRD #362 Slice A); plain data, captured
   * verbatim (the optional fixed destination deep-copied). Additive-optional like
   * `dice`/`rat` before it, so it needs **no schema bump** - an older save simply
   * has no gates.
   */
  moongate?: MoongateComponent;
  /**
   * Treasure-chest state for chest entities (PRD #362 Slice B); plain data,
   * captured verbatim. Additive-optional like `dice` before it - no schema bump.
   */
  chest?: ChestComponent;
  /**
   * Banishment countdown for an island castaway (PRD #362 Slice D); plain data,
   * captured verbatim. Additive-optional - no schema bump; a sentence is
   * server-event state, so a restored save simply shows the last-pushed clock.
   */
  banish?: BanishComponent;
  /**
   * Rat-Swarm marker for rat entities (PRD #222); plain data, captured verbatim.
   * Additive-optional like `dice` before it, so it needs **no schema bump**: an
   * older save simply has no rats, and a save with rats restores them as the
   * stompable mobiles they were.
   */
  rat?: true;
  /**
   * Health vital for mobiles that carry one (Duel slice A, ADR-0128); plain data,
   * captured verbatim. Additive-optional like `rat`/`dice` before it, so it needs
   * **no schema bump** - an older save simply has no health and restores unhealed
   * mobiles. Never persisted to the account store, only to this world snapshot.
   */
  health?: HealthComponent;
  /**
   * Blackthorn's-Storm vortex marker for vortex entities (PRD #289 Slice C);
   * plain data, captured verbatim. Additive-optional like `rat` before it, so it
   * needs **no schema bump**.
   */
  vortex?: true;
  /**
   * Arena horde-enemy marker (PRD #334 Slice B); plain data, captured verbatim.
   * Additive-optional like `rat`/`vortex` before it, so it needs **no schema
   * bump**. An arena world is torn down at run end and never meaningfully
   * persisted, but a mid-run snapshot still round-trips the marker.
   */
  horde?: true;
  /** Private dungeon enemy marker for continuous server-owned locomotion. */
  dungeonEnemy?: true;
  /**
   * Death bookkeeping (remembered living body) for a downed mobile the shared
   * damage/death seam swapped onto a ghost (ADR-0150); plain data, captured
   * verbatim. Additive-optional like `rat`/`health` before it, so it needs **no
   * schema bump** - a duel/Storm death is transient, but a mid-death snapshot
   * still round-trips the swap-back data.
   */
  death?: DeathComponent;
  /**
   * The King's Justice NPC role marker (thief/guard, PRD #361, ADR-0194); plain
   * data, captured verbatim. Additive-optional like `rat`/`vortex` before it, so
   * it needs **no schema bump** - the event is ephemeral, but the marker must ride
   * the wire snapshot for the client's scenery/loot gating.
   */
  justice?: 'thief' | 'guard';
  /**
   * The fallen thief's lootable-corpse marker (PRD #361, ADR-0194); plain data,
   * captured verbatim. Additive-optional, **no schema bump**.
   */
  thiefCorpse?: true;
  /**
   * The Bank Escort's replicated payload (escorter id while led, PRD #371);
   * plain data, captured verbatim. Additive-optional, **no schema bump** - the
   * escort is ephemeral, but the component must ride the wire snapshot for the
   * client's scenery gating + menu rows.
   */
  escort?: EscortComponent;
  /**
   * The Arena Master's herald marker (PRD #334, ADR-0209); plain data, captured
   * verbatim. Additive-optional like `escort`/`rat` before it, **no schema bump**
   * - he is boot-spawned world content, but the marker must ride the wire snapshot
   * for the client's scenery gating + "Join the arena" menu row.
   */
  arenaMaster?: true;
  /**
   * A flying arena projectile (bullet-hell rework, PRD #399 Slice A); plain data,
   * captured verbatim. Additive-optional like `horde`/`vortex` before it, so it
   * needs **no schema bump** - an arena run is torn down and never meaningfully
   * persisted, but a mid-run snapshot still round-trips the in-flight bolts.
   */
  projectile?: ProjectileComponent;
  /**
   * A dropped arena XP gem (VS-style orbs); plain data, captured verbatim.
   * Additive-optional like `projectile` before it, so it needs **no schema
   * bump** - gems live only in ephemeral run worlds, but a mid-run snapshot
   * still round-trips the ground drops.
   */
  xpGem?: XpGemComponent;
  /**
   * A dropped arena pickup (PRD #431 Vacuum); plain data, captured verbatim.
   * Additive-optional like `xpGem` before it, so it needs **no schema bump** -
   * pickups live only in ephemeral run worlds, but a mid-run snapshot still
   * round-trips the ground drop.
   */
  pickup?: PickupComponent;
  /**
   * Replicated interaction role (PRD #534 D5, ADR-0266); plain data, captured
   * verbatim. Additive-optional like `rat`/`health` before it - **no schema
   * bump**; an older save simply restores role-less entities, which the client
   * reads as fail-safe scenery.
   */
  interaction?: InteractionComponent;
  /** Private dungeon-run ground loot, captured verbatim in run snapshots. */
  dungeonLoot?: DungeonLootComponent;
  /** Private dungeon-run health globe, captured verbatim in run snapshots. */
  dungeonGlobe?: DungeonGlobeComponent;
  /** Private dungeon-run campaign waypoint, captured verbatim in run snapshots. */
  dungeonWaypoint?: DungeonWaypointComponent;
  /** Bone Knight Wall looping training pose, captured verbatim (PRD #837). */
  trainingLoop?: TrainingLoopComponent;
  /** Bone Knight Wall greeter marker, captured verbatim (PRD #837). */
  soulstoneKeeper?: true;
}

/**
 * Current save-schema version. Bump this whenever the world/entity shape changes
 * in a way that makes older saves wrong rather than merely incomplete. A save
 * stamped with any other version is discarded on load - {@link deserialize}
 * throws - so a stale save can never silently resume into a world it predates.
 *
 * v1: the quarried walk-around shape (position + renderable + mobile only).
 */
export const SCHEMA_VERSION = 1;

/**
 * How to shape a {@link serialize} pass.
 *
 * `wire: true` selects each component descriptor's optional wire shape (PRD
 * #825) - the lean form the broadcast sends - instead of its full save shape.
 * The two are interchangeable to {@link deserialize}: both rebuild through the
 * same descriptor, so a client fed the wire form and a server loading a save
 * take one reconstruction path. Default (`false`) is the lossless save shape.
 */
export interface SerializeOptions {
  wire?: boolean;
}

/** The full, JSON-able snapshot of a world. */
export interface WorldSnapshot {
  /** Save-schema version; see {@link SCHEMA_VERSION}. */
  version?: number;
  /** This project's own map format (palette + cells + walkability + statics). */
  map: MapData;
  /** The seeded RNG's internal state, so the restored stream resumes in place. */
  rngState: number;
  /** The id the restored world's next {@link World.allocateId} will return. */
  idCounter: number;
  /** Entities in insertion order, each with all its components. */
  entities: EntitySnapshot[];
  /** Pending (undrained) commands, in FIFO order. */
  commandQueue: Command[];
}

/**
 * Capture one entity as plain data by walking the component descriptor table
 * (`src/sim/componentDescriptors.ts`, ADR-0260) - each present component is
 * serialized by its declared `serialize` shape, in table order. The table's
 * declaration order is the historical hand-written key order, so the JSON
 * output is byte-identical to what the old per-component lines produced; a
 * component missing from the table is a **compile error**, so a new component
 * can never again silently vanish from saves (the persistence-serializer
 * gotcha this table exists to kill).
 */
function serializeEntity(e: Entity, wire: boolean): EntitySnapshot {
  const snap: EntitySnapshot = { id: e.id };
  for (const key of COMPONENT_KEYS) {
    const live = e[key];
    if (!live) continue;
    const descriptor = COMPONENT_DESCRIPTORS[key];
    // A descriptor may declare a leaner wire shape (PRD #825); the save path
    // always takes the full `serialize`.
    const shape = (wire && descriptor.wire) || descriptor.serialize;
    (snap as unknown as Record<string, unknown>)[key] = (shape as (v: unknown) => unknown)(live);
  }
  return snap;
}

/**
 * Capture the whole world as a plain snapshot. Every per-entity component is
 * deep-copied so the snapshot is independent of subsequent world mutation; the
 * result is safe to `JSON.stringify`.
 *
 * The one deliberate exception is the `map`: it is taken **by reference** from
 * {@link GameMap.toData} (no copy on this hot path - `serialize()` runs every
 * keyframe cadence). That is sound because a {@link GameMap} deep-freezes its
 * backing {@link MapData} on construction, so the shared data is immutable for the
 * life of the world - the alias can never observe a mutation that the entity
 * deep-copies guard against. `deserialize()` rebuilds the map from a fresh,
 * JSON-parsed copy, so distinct loads never share map state.
 */
export function serialize(world: World, opts: SerializeOptions = {}): WorldSnapshot {
  const wire = opts.wire === true;
  return {
    version: SCHEMA_VERSION,
    map: world.map.toData(),
    rngState: world.rng.getState(),
    idCounter: world.idCounter,
    entities: world.all().map((e) => serializeEntity(e, wire)),
    commandQueue: world.pendingCommands.map((c) => ({ ...c })),
  };
}

/**
 * Per-{@link MapData} {@link GameMap} cache (perf, ADR-0132). The browser rebuilds
 * a read-model {@link World} from a snapshot on **every** broadcast frame (10Hz since
 * ADR-0163's broadcast-cadence drop; 20Hz before it) via
 * `snapshotToWorld` -> {@link deserialize}; naively that reran `new GameMap(data.map)`
 * each frame, which re-freezes the data and rebuilds the ~30k-key `roofCells` /
 * `staticTopByTile` structures - ~3.2ms per message, the dominant client cost, plus
 * the GC churn that reads as lag.
 *
 * But across a delta/keyframe stream the {@link MapData} travels forward **by
 * reference** (`applyDelta`/`applyKeyframe` carry `base.map` unchanged) and is
 * deep-frozen, so the {@link GameMap} built from it is immutable and identical every
 * frame. Keyed on the MapData reference in a {@link WeakMap}, we build the GameMap
 * once and reuse it until a new map-bearing `Snapshot` brings a fresh reference (then
 * the old entry GCs with its key). Sharing one immutable GameMap across successive
 * read-model Worlds is safe: nothing mutates a GameMap after construction, and a
 * server load gets a fresh JSON-parsed MapData reference, so distinct loads never
 * collide in the cache.
 */
const gameMapByData = new WeakMap<MapData, GameMap>();

/** The memoized {@link GameMap} for this {@link MapData} reference; see {@link gameMapByData}. */
function gameMapFor(data: MapData): GameMap {
  let map = gameMapByData.get(data);
  if (map === undefined) {
    map = new GameMap(data);
    gameMapByData.set(data, map);
  }
  return map;
}

/**
 * The exact mirror of {@link serializeEntity}: rebuild each present component
 * by its descriptor's `rebuild` shape (the same clone for plain data; the live
 * {@link Mobile} reconstructs via `Mobile.fromState`). Driven by the same
 * table, so the two sides can never drift.
 */
function rebuildEntity(snap: EntitySnapshot): Entity {
  const e: Entity = { id: snap.id };
  for (const key of COMPONENT_KEYS) {
    const value = snap[key];
    if (!value) continue;
    (e as unknown as Record<string, unknown>)[key] = (
      COMPONENT_DESCRIPTORS[key].rebuild as (v: unknown) => unknown
    )(value);
  }
  return e;
}

/**
 * Reconstruct an equivalent live {@link World} from a snapshot. The result is
 * deep-equal to the world that produced the snapshot: same map, same entities
 * (including mid-step mobiles), same RNG stream position, same id allocator, and
 * the same pending command queue.
 */
export function deserialize(data: WorldSnapshot): World {
  if (data.version !== SCHEMA_VERSION) {
    throw new Error(
      `Incompatible save version ${data.version ?? '(none)'}, expected ${SCHEMA_VERSION}`,
    );
  }
  const world = new World({ map: gameMapFor(data.map), seed: data.rngState });
  // The constructor seeds the RNG from `rngState`, but the stream may have been
  // advanced since seeding; restore the exact internal state in place.
  world.rng.setState(data.rngState);
  for (const snap of data.entities) world.add(rebuildEntity(snap));
  // Restore the allocator after adding, since `add` does not consume ids.
  world.idCounter = data.idCounter;
  // Re-enqueue pending commands in their original FIFO order.
  for (const command of data.commandQueue) world.enqueue(command);
  return world;
}
